// Loan-detail shared attention strip. Renders count chips (status-token
// tinted) for things that need eyes — lapsed policies, expiring items,
// upcoming due dates, open claims, … Clicking a chip expands an item list
// below it (owner + formatted date); clicking again collapses. Chips with
// count 0 are hidden; the strip renders nothing when every count is zero.
//
// Hiding a zero is correct HERE and nowhere near universal: this strip's whole
// job is "what needs eyes", so a chip reading 0 is a line of furniture saying
// nothing needs them. A strip that always rendered every category would make
// the empty case as loud as the urgent one. Where a measured zero IS the
// information — a filter that must keep its shape — use `FilterChips`, which
// renders the zero.
//
// `onChipClick` turns the chips into navigation instead of disclosure; see the
// prop for when that is the right shape.

import { useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/design-system/lib/utils";
import { formatCalendarDate } from "@/design-system/lib/format";

export interface AttentionChip {
  id: string;
  label: string;
  count: number;
  tone: "error" | "warning" | "neutral";
  items: {
    id: string;
    title: string;
    owner?: string | null;
    date?: string | null;
    /** Optional click-through (e.g. the owning loan tab). */
    href?: string | null;
  }[];
}

const TONE_CHIP_CLASS: Record<AttentionChip["tone"], string> = {
  error:
    "border-status-error bg-status-error/10 text-status-error",
  warning:
    "border-status-warning bg-status-warning/10 text-status-warning",
  neutral: "border-border-light bg-white-warm text-gray-mid",
};

const TONE_DOT_CLASS: Record<AttentionChip["tone"], string> = {
  error: "bg-status-error",
  warning: "bg-status-warning",
  neutral: "bg-gray-mid",
};

export function AttentionStrip({
  chips,
  testId,
  onChipClick,
}: {
  chips: AttentionChip[];
  testId?: string;
  /**
   * Makes a chip a NAVIGATION rather than a disclosure.
   *
   * The default behaviour — expand a list of the counted items underneath —
   * is right where the strip is the only place those items appear. It is the
   * wrong shape where the counted things are already on the page behind a
   * filter: there the useful click is "take me to them", and expanding a
   * second copy of a list the reader can already see is furniture.
   *
   * Supplying this replaces the expansion entirely (no half-state where a
   * click both navigates and opens something), and OMITTING it leaves the
   * expansion exactly as it was — which the portfolio surfaces rely on.
   */
  onChipClick?: (chipId: string) => void;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const visible = chips.filter((c) => c.count > 0);
  if (visible.length === 0) return null;

  const expanded = onChipClick
    ? null
    : (visible.find((c) => c.id === expandedId) ?? null);

  return (
    <div className="flex flex-col gap-space-2" data-testid={testId}>
      <div className="flex items-center gap-space-2 flex-wrap" role="list">
        {visible.map((chip) => {
          const isExpanded = chip.id === expanded?.id;
          return (
            <button
              key={chip.id}
              type="button"
              role="listitem"
              aria-expanded={onChipClick ? undefined : isExpanded}
              onClick={() =>
                onChipClick
                  ? onChipClick(chip.id)
                  : setExpandedId((prev) => (prev === chip.id ? null : chip.id))
              }
              className={cn(
                "inline-flex items-center gap-space-2 px-space-3 py-space-1 rounded-full border text-small font-display font-semibold transition-colors",
                TONE_CHIP_CLASS[chip.tone],
                isExpanded && "ring-1 ring-current",
              )}
              data-testid={testId ? `${testId}-chip-${chip.id}` : undefined}
            >
              <span
                className={cn(
                  "inline-block h-2 w-2 rounded-full",
                  TONE_DOT_CLASS[chip.tone],
                )}
                aria-hidden
              />
              {chip.label}
              <span className="font-mono">{chip.count}</span>
            </button>
          );
        })}
      </div>
      {expanded && (
        <ul
          className="m-0 list-none flex flex-col gap-space-1 rounded-md border border-border-light bg-white-pure p-space-3"
          data-testid={testId ? `${testId}-items` : undefined}
        >
          {expanded.items.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between gap-space-3 text-small text-onyx"
            >
              {item.href ? (
                <Link
                  to={item.href}
                  className="min-w-0 truncate text-onyx hover:text-gold-shadow hover:underline"
                >
                  {item.title}
                </Link>
              ) : (
                <span className="min-w-0 truncate">{item.title}</span>
              )}
              <span className="shrink-0 text-caption text-gray-mid">
                {item.owner ? `${item.owner} · ` : ""}
                {item.date ? formatCalendarDate(item.date) : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
